'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createVideoWarmupQueue } from '@/lib/home-video-warmup';

const HomeMediaContext = createContext({ active: true, allowMotion: true });

// One set of preference/page listeners for the entire homepage.
export function HomeMediaProvider({ suspended = false, children }) {
  const [preferences, setPreferences] = useState({ active: false, allowMotion: false });
  const enqueue = useMemo(() => createVideoWarmupQueue(3), []);
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    const sync = () => setPreferences({
      active: !document.hidden,
      allowMotion: !motion.matches && !connection?.saveData,
    });
    sync();
    document.addEventListener('visibilitychange', sync);
    motion.addEventListener('change', sync);
    connection?.addEventListener?.('change', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      motion.removeEventListener('change', sync);
      connection?.removeEventListener?.('change', sync);
    };
  }, []);
  const value = useMemo(() => ({ ...preferences, active: preferences.active && !suspended, enqueue }), [enqueue, preferences, suspended]);
  return <HomeMediaContext.Provider value={value}>{children}</HomeMediaContext.Provider>;
}

/** Homepage policy; Explore's existing media surface is intentionally independent. */
export default function HomeVideo({
  src,
  poster,
  className = '',
  autoPlay = false,
  hoverIntent = false,
  nearby = true,
  nearRootRef,
  paused = false,
  ...props
}) {
  const ref = useRef(null);
  const { active, allowMotion, enqueue } = useContext(HomeMediaContext);
  const [visible, setVisible] = useState(false);
  const [nearViewport, setNearViewport] = useState(false);
  const [attached, setAttached] = useState(false);
  const [warming, setWarming] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const releaseRef = useRef(null);
  const wantedRef = useRef(false);
  const canRun = active && allowMotion && !paused;
  const shouldPlay = canRun && visible && (autoPlay || hoverIntent);
  const shouldWarm = canRun && nearby && nearViewport;
  wantedRef.current = shouldPlay;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const visibleObserver = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.05);
    }, { threshold: [0, 0.05] });
    // Observe the rail for vertical proximity: a horizontal scroller clips its
    // offscreen children even with rootMargin, so it cannot prewarm neighbours.
    const nearObserver = new IntersectionObserver(([entry]) => {
      setNearViewport(entry.isIntersecting);
    }, { rootMargin: '450px 0px' });
    visibleObserver.observe(node);
    nearObserver.observe(nearRootRef?.current || node);
    return () => { visibleObserver.disconnect(); nearObserver.disconnect(); };
  }, [nearRootRef]);

  const finishWarmup = useCallback(() => {
    setWarming(false);
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);

  useEffect(() => {
    if (shouldPlay && !autoPlay && enqueue) return enqueue.hold();
    return undefined;
  }, [autoPlay, enqueue, shouldPlay]);

  useEffect(() => {
    if (!shouldWarm || shouldPlay || ready || failed || !enqueue) return undefined;
    let timeout;
    const cancel = enqueue(src, (release) => {
      releaseRef.current = release;
      setAttached(true);
      setWarming(true);
      // An unavailable asset must not occupy a speculative slot indefinitely.
      timeout = window.setTimeout(finishWarmup, 8000);
    });
    return () => {
      window.clearTimeout(timeout);
      cancel();
      releaseRef.current = null;
      setWarming(false);
    };
  }, [enqueue, failed, finishWarmup, ready, shouldPlay, shouldWarm, src]);

  const attemptPlayback = useCallback(() => {
    const node = ref.current;
    if (!node || !wantedRef.current || !node.getAttribute('src')) return;
    if (node.error) {
      setFailed(false);
      node.load(); // Retry a failed resource only on a new playback attempt.
    }
    node.play().catch((error) => {
      // Leaving a card may cancel a pending play; source/decoder errors stay
      // observable and retain the poster instead of masquerading as loaded.
      if (error.name !== 'AbortError' && wantedRef.current) {
        node.dataset.playbackError = error.name;
      }
    });
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (shouldPlay) {
      setAttached(true);
      attemptPlayback();
    } else {
      node.pause();
    }
  }, [attached, attemptPlayback, shouldPlay]);

  useEffect(() => {
    if (shouldWarm || shouldPlay || !attached) return undefined;
    // Keep nearby/recent buffers for backtracking; release distant players later.
    const timeout = window.setTimeout(() => {
      const node = ref.current;
      node?.pause();
      node?.removeAttribute('src');
      node?.load();
      setAttached(false);
      setReady(false);
      setFailed(false);
    }, 30_000);
    return () => window.clearTimeout(timeout);
  }, [attached, shouldPlay, shouldWarm]);

  return (
    <video
      {...props}
      ref={ref}
      data-home-video="true"
      data-src={src}
      data-ready={ready}
      src={attached ? src : undefined}
      poster={poster || undefined}
      className={className}
      muted
      loop
      playsInline
      preload={attached ? ((shouldPlay || warming) ? 'auto' : 'metadata') : 'none'}
      onLoadedData={() => { setReady(true); finishWarmup(); }}
      onCanPlay={() => { setReady(true); finishWarmup(); attemptPlayback(); }}
      onPlaying={(event) => {
        if (!wantedRef.current) event.currentTarget.pause();
        else delete event.currentTarget.dataset.playbackError;
      }}
      onError={() => { setFailed(true); setReady(false); finishWarmup(); }}
    />
  );
}
